import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Users, ShoppingCart, DollarSign, List } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dashboardService from '../services/dashboardService';
import SalesChart from './SalesChart';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = 'overview';
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    pendingApprovals: 0,
    totalRevenue: 0
  });

  // Dynamic sales data
  const [salesData, setSalesData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  // Date range state (DEFAULT: Last 7 days)
  const initialStart = Number(searchParams.get('start')) || Date.now() - (7 * 24 * 60 * 60 * 1000);
  const initialEnd = Number(searchParams.get('end')) || Date.now();
  const [dateRange, setDateRange] = useState({ start_date: initialStart, end_date: initialEnd });

  const [startDate, setStartDate] = useState(new Date(initialStart));
  const [endDate, setEndDate] = useState(new Date(initialEnd));
  const [activeFilter, setActiveFilter] = useState(() => searchParams.get('range') || '7days');

  useEffect(() => {
    fetchStats();
    // Fetch chart data on initial load
    if (activeTab === 'overview') {
      fetchSalesChart();
    }
  }, []);

  // Fetch chart data when date range changes
  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('range', activeFilter);
      next.set('start', String(dateRange.start_date));
      next.set('end', String(dateRange.end_date));
      return next;
    }, { replace: true });
    if (activeTab === 'overview') {
      console.log('📊 Fetching chart for activeFilter:', activeFilter);
      fetchSalesChart();
    }
  }, [dateRange, activeTab]);

  const fetchStats = async () => {
    try {
      // Use the new dashboard service
      const statsData = await dashboardService.getStats();

      setStats({
        totalProducts: statsData.totalProducts,
        totalOrders: statsData.totalOrders,
        pendingApprovals: statsData.pendingApprovals,
        totalRevenue: parseFloat(statsData.totalRevenue)
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSalesChart = async () => {
    try {
      setLoadingChart(true);
      const chartData = await dashboardService.getSalesChart(
        dateRange.start_date,
        dateRange.end_date
      );
      setSalesData(chartData);
    } catch (error) {
      console.error('Error fetching sales chart:', error);
      console.error('Error details:', error.message);
      setSalesData([]); // Show empty chart on error
    } finally {
      setLoadingChart(false);
    }
  };

  const handlePresetRange = (days, filterName) => {
    console.log(`🔘 Setting filter to: ${filterName}`);
    const end = Date.now();
    const start = end - (days * 24 * 60 * 60 * 1000);
    setDateRange({ start_date: start, end_date: end });
    setStartDate(new Date(start));
    setEndDate(new Date(end));
    setActiveFilter(filterName);
  };

  const handleCustomDateChange = () => {
    // Validate date range
    if (startDate > endDate) {
      alert('Start date must be before end date');
      return;
    }
    setDateRange({
      start_date: startDate.getTime(),
      end_date: endDate.getTime()
    });
    setActiveFilter('custom');
  };

  return (
    <div className="dashboard">
      {activeTab === 'overview' && (
        <>
          {/* Compact KPI cards */}
          <div className="dashboard-stats-minimal">
            <div className="stat-card-minimal">
              <div className="stat-minimal-icon">
                <Package size={20} />
              </div>
              <div className="stat-minimal-content">
                <p className="stat-minimal-label">Total Products</p>
                <h3 className="stat-minimal-value">{stats.totalProducts}</h3>
              </div>
            </div>

            <div className="stat-card-minimal">
              <div className="stat-minimal-icon">
                <ShoppingCart size={20} />
              </div>
              <div className="stat-minimal-content">
                <p className="stat-minimal-label">Total Orders</p>
                <h3 className="stat-minimal-value">{stats.totalOrders}</h3>
              </div>
            </div>

            <div className="stat-card-minimal">
              <div className="stat-minimal-icon">
                <Users size={20} />
              </div>
              <div className="stat-minimal-content">
                <p className="stat-minimal-label">Pending Approvals</p>
                <h3 className="stat-minimal-value">{stats.pendingApprovals}</h3>
              </div>
            </div>

            <div className="stat-card-minimal">
              <div className="stat-minimal-icon">
                <DollarSign size={20} />
              </div>
              <div className="stat-minimal-content">
                <p className="stat-minimal-label">Total Revenue</p>
                <h3 className="stat-minimal-value">₹{stats.totalRevenue.toLocaleString()}</h3>
              </div>
            </div>
          </div>

          {/* Sales Chart with Dynamic Data */}
          <Card>
            <CardHeader>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div>
                  <CardTitle>Total Sales</CardTitle>
                  <CardDescription>Sales performance over time</CardDescription>
                </div>

                {/* Date Range Filters */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  {/* Preset Filters */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Button
                      key="7days"
                      variant={activeFilter === '7days' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        console.log('Clicked 7days, activeFilter was:', activeFilter);
                        handlePresetRange(7, '7days');
                      }}
                      title={`Current filter: ${activeFilter}`}
                    >
                      Last 7 Days
                    </Button>
                    <Button
                      key="30days"
                      variant={activeFilter === '30days' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handlePresetRange(30, '30days')}
                    >
                      Last 30 Days
                    </Button>
                    <Button
                      key="90days"
                      variant={activeFilter === '90days' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handlePresetRange(90, '90days')}
                    >
                      Last 90 Days
                    </Button>
                    <Button
                      key="365days"
                      variant={activeFilter === '365days' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handlePresetRange(365, '365days')}
                    >
                      Last Year
                    </Button>
                  </div>

                  {/* Custom Date Picker */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '0px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    background: 'white'
                  }}>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Start"
                      className="date-picker-input"
                    />
                    <span style={{ color: '#64748b' }}>to</span>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="End"
                      className="date-picker-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleCustomDateChange}
                      variant={activeFilter === 'custom' ? 'default' : 'outline'}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {loadingChart ? (
                <div style={{
                  height: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}>
                  Loading chart data...
                </div>
              ) : salesData.length > 0 ? (
                <SalesChart data={salesData} />
              ) : (
                <div style={{
                  height: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b'
                }}>
                  No sales data available for the selected period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions - Minimal */}
          <div className="quick-actions-minimal">
            <button
              className="action-btn-minimal"
              onClick={() => navigate('/orders')}
            >
              <List size={18} />
              <span>View Orders</span>
            </button>

            <button
              className="action-btn-minimal"
              onClick={() => navigate('/users')}
            >
              <Users size={18} />
              <span>Manage Users</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
